import { Logo } from '@common/components/brand/Logo';
import { LanguageMenu } from '@common/components/layout/LanguageMenu';
import { ThemeMenu } from '@common/components/layout/ThemeMenu';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks';
import { consumeRedirect } from '../utils/redirectAfterLogin';
import { LoginForm } from './LoginForm';

function safeRedirect(path: string | null): string {
  if (!path?.startsWith('/') || path.startsWith('//') || path === '/login') return '/';
  return path;
}

export function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  const { isAuthenticated, isLoading } = useAuth();
  const [params] = useSearchParams();

  useEffect(() => {
    const original = document.title;
    document.title = `${t('auth:loginTitle')} · ${t('common:appName')}`;
    return () => {
      document.title = original;
    };
  }, [t]);

  if (!isLoading && isAuthenticated) {
    const target = safeRedirect(params.get('from') ?? consumeRedirect());
    return <Navigate to={target} replace />;
  }

  return (
    <main className="grid min-h-dvh md:grid-cols-2">
      <aside
        className="relative hidden flex-col justify-between overflow-hidden p-12 text-brand-primary-foreground md:flex"
        style={{
          background:
            'linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-primary-active) 100%)',
        }}
      >
        <div aria-hidden className="pointer-events-none absolute -right-32 -bottom-40 opacity-15">
          <svg
            width="520"
            height="520"
            viewBox="0 0 48 49"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <title>NTConsult</title>
            <path d="M25.096 2.75934V25.3879H2.71308V48.0165H47.4789V2.75934H25.096Z" />
            <path d="M22.3829 0.0164795H0V22.6451H22.3829V0.0164795Z" fillOpacity="0.7" />
          </svg>
        </div>

        <div className="relative z-10">
          <Logo size="lg" tone="inverted" />
        </div>

        <div className="relative z-10 flex max-w-md flex-col gap-3">
          <h2 className="text-balance text-4xl font-semibold leading-tight tracking-tight">
            {t('common:appName')}
          </h2>
          <p className="text-pretty text-lg opacity-90">{t('auth:tagline')}</p>
        </div>

        <div className="relative z-10 text-xs opacity-70">
          © {new Date().getFullYear()} NTConsult
        </div>
      </aside>

      <section className="flex flex-col bg-surface">
        <header className="flex items-center justify-end gap-1 p-4">
          <LanguageMenu />
          <ThemeMenu />
        </header>

        <div className="flex flex-1 items-center justify-center px-6 pb-6">
          <div className="flex w-full max-w-sm flex-col gap-7">
            <div className="flex flex-col items-center gap-3 md:hidden">
              <Logo size="lg" />
            </div>

            <div className="flex flex-col gap-1.5">
              <h1 className="text-title text-foreground">{t('auth:loginTitle')}</h1>
              <p className="text-sm text-foreground-muted">{t('auth:loginSubtitle')}</p>
            </div>

            <LoginForm />
          </div>
        </div>

        <footer className="border-t border-border px-6 py-3 text-center text-xs text-foreground-muted md:text-right">
          v{__APP_VERSION__}
        </footer>
      </section>
    </main>
  );
}
